// /dashboard/admin/roles — list page.
// Spec: docs/specs/custom-rbac.md §6.1.
//
// Two sections: 系統預設（不可編輯）+ 自訂角色。
// 系統 roles are seeded with tenant_id = NULL and surface read-only;
// custom roles support 編輯 / 刪除.

import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, RefreshCw, ServerCrash, ShieldCheck, Trash2 } from "lucide-react";
import PageHeader from "../../components/PageHeader.js";
import EmptyState from "../../components/EmptyState.js";
import { useConfirmDialog } from "../../components/ConfirmDialog.js";
import { useDashboardSession } from "../../auth/useDashboardSession.js";
import TenantPickerEmptyState from "../../components/TenantPickerEmptyState.js";
import { listRoles, deleteRole, friendlyRolesError, type RoleRow } from "../../lib/rolesApi.js";

type ListState =
  | { status: "loading" }
  | { status: "ready"; rows: RoleRow[] }
  | { status: "error"; message: string };

export default function RolesList(): React.ReactElement {
  const session = useDashboardSession();
  const tenantParam = session.super_admin ? session.acting_tenant_id ?? null : null;
  const [state, setState] = useState<ListState>({ status: "loading" });
  const dlg = useConfirmDialog();

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const r = await listRoles(tenantParam);
      setState({ status: "ready", rows: r.items });
    } catch (err) {
      setState({ status: "error", message: friendlyRolesError(err) });
    }
  }, [tenantParam]);

  useEffect(() => { void load(); }, [load]);

  async function handleDelete(row: RoleRow): Promise<void> {
    const ok = await dlg.confirm(
      `確認刪除自訂角色「${row.name}」？此操作無法復原。`,
      { destructive: true, okLabel: "刪除" },
    );
    if (!ok) return;
    try {
      await deleteRole(row.id, tenantParam);
      await load();
    } catch (err) {
      await dlg.alert(friendlyRolesError(err), { title: "刪除失敗" });
    }
  }

  if (session.super_admin && !tenantParam) {
    return (
      <>
        <PageHeader title="角色與權限" description="管理系統預設角色與自訂角色的權限組合" />
        <TenantPickerEmptyState />
      </>
    );
  }

  if (state.status === "error") {
    return (
      <>
        <PageHeader title="角色與權限" />
        <EmptyState
          icon={ServerCrash}
          title="無法載入角色列表"
          description={state.message}
          action={
            <button onClick={() => void load()} className="btn-secondary text-sm">
              <RefreshCw className="h-4 w-4" /> 重試
            </button>
          }
        />
      </>
    );
  }

  const rows = state.status === "ready" ? state.rows : [];
  const systemRoles = rows.filter((r) => r.is_system);
  const customRoles = rows.filter((r) => !r.is_system);

  return (
    <>
      <PageHeader
        title="角色與權限"
        description="管理系統預設角色與自訂角色的權限組合。系統預設不可編輯，需要客製時請建立自訂角色。"
        actions={
          <>
            <button
              type="button"
              onClick={() => void load()}
              className="btn-ghost text-sm"
              aria-label="重新載入"
            >
              <RefreshCw className={`h-4 w-4 ${state.status === "loading" ? "animate-spin" : ""}`} />
            </button>
            <Link to="/dashboard/admin/roles/new" className="btn-primary text-sm">
              <Plus className="h-4 w-4" /> 建立角色
            </Link>
          </>
        }
      />

      {/* System roles */}
      <section className="mb-6">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-text-muted mb-2">
          系統預設（不可編輯）
        </h2>
        {systemRoles.length === 0 && state.status === "loading" ? (
          <div className="rounded-xl border border-border bg-bg-surface p-6 text-center text-2xs text-text-muted">
            載入中…
          </div>
        ) : (
          <div className="rounded-xl border border-border bg-bg-surface divide-y divide-border">
            {systemRoles.map((r) => (
              <RoleRowItem key={r.id} row={r} />
            ))}
          </div>
        )}
      </section>

      {/* Custom roles */}
      <section>
        <h2 className="text-xs font-semibold uppercase tracking-wider text-text-muted mb-2">
          自訂角色（{customRoles.length}）
        </h2>
        {customRoles.length === 0 ? (
          <EmptyState
            icon={ShieldCheck}
            title="尚未建立任何自訂角色"
            description="自訂角色可以從系統預設複製後微調權限，或從空白開始自訂。"
            action={
              <Link to="/dashboard/admin/roles/new" className="btn-primary text-sm">
                <Plus className="h-4 w-4" /> 建立第一個自訂角色
              </Link>
            }
          />
        ) : (
          <div className="rounded-xl border border-border bg-bg-surface divide-y divide-border">
            {customRoles.map((r) => (
              <RoleRowItem key={r.id} row={r} onDelete={() => void handleDelete(r)} />
            ))}
          </div>
        )}
      </section>
    </>
  );
}

function RoleRowItem({ row, onDelete }: { row: RoleRow; onDelete?: () => void }): React.ReactElement {
  return (
    <div className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <span className={[
          "flex h-9 w-9 items-center justify-center rounded-lg flex-shrink-0",
          row.is_system ? "bg-accent-subtle text-accent" : "bg-bg-raised text-text-secondary border border-border",
        ].join(" ")}>
          <ShieldCheck className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-text-primary truncate">{row.name}</div>
          {row.description && (
            <div className="text-2xs text-text-muted truncate">{row.description}</div>
          )}
          {row.cloned_from && (
            <div className="text-2xs text-text-muted mt-0.5">複製自 <code className="font-mono">{row.cloned_from}</code></div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-3 flex-shrink-0 pl-12 sm:pl-0 sm:gap-4">
        <span className="text-2xs text-text-secondary font-mono whitespace-nowrap sm:w-24 sm:text-right">
          {row.permission_count} 個權限
        </span>
        <span className="text-2xs text-text-secondary font-mono whitespace-nowrap sm:w-24 sm:text-right">
          {row.assigned_user_count} 位使用者
        </span>
        <Link
          to={`/dashboard/admin/roles/${encodeURIComponent(row.id)}`}
          className="btn-ghost text-xs ml-auto sm:ml-0"
        >
          {row.is_system ? "檢視" : "編輯"}
        </Link>
        {onDelete && (
          <button
            type="button"
            onClick={onDelete}
            className="btn-ghost text-xs text-danger"
            title="刪除自訂角色"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
